import React, { useState, useEffect } from 'react';
import Select, { MultiValue, ActionMeta } from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toAbsoluteUrl } from '../../../_metronic/helpers';
import { Link, useHistory } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

type Shipment = {
  shipment_id: string;
  name: string;
};

type ShipmentOption = {
  value: string;
  label: string;
};

type ExpenseOption = {
  value: string;
  label: string;
};

type ExpenseListOption = {
  value: string;
  label: string;
};

type ExpenseTableData = {
  id: string;
  expenseName: string;
  amount: string;
  comment: string;
  spendDate: Date | null; // Added spendDate
};

export default function ExpensesForm() {
  const [shipments, setShipments] = useState<ShipmentOption[]>([]);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentOption | null>(null);
  const [expenseType, setExpenseType] = useState<ExpenseOption | null>(null);
  const [directExpenseOptions, setDirectExpenseOptions] = useState<ExpenseListOption[]>([]);
  const [indirectExpenseOptions, setIndirectExpenseOptions] = useState<ExpenseListOption[]>([]);
  const [selectedDirectExpenses, setSelectedDirectExpenses] = useState<ExpenseListOption[]>([]);
  const [selectedIndirectExpenses, setSelectedIndirectExpenses] = useState<ExpenseListOption[]>([]);
  const [expenseTableData, setExpenseTableData] = useState<ExpenseTableData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const history = useHistory();

  const [success, setSuccess] = useState(false);

  // Options for Expense Type
  const expenseOptions: ExpenseOption[] = [
    { value: 'Direct Expense', label: 'Direct Expense' },
    { value: 'Indirect Expense', label: 'Indirect Expense' },
  ];


  // Generate unique ID
  const generateId = () => {
    return Math.random().toString(36).substring(2, 9);
  };


  // Fetch direct and indirect expenses
  useEffect(() => {
    const fetchDirectExpenses = async () => {
      try {
        const response = await fetch('https://backendcway.causewaycargo.com/superadmin/directexpense', {
          method: 'GET',
          headers: {
            auth: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
          },
        });
        const result = await response.json();
        console.log('Direct Expenses Data:', result); // Log the full result
        const data = result.data; // Access the 'data' property
        if (Array.isArray(data)) {
          const directOptions = data.map((expense: any) => ({
            value: expense.id,
            label: expense.expenses_type,
          }));
          setDirectExpenseOptions(directOptions);
        } else {
          console.error('Direct expenses data is not an array:', data);
        }
      } catch (error) {
        console.error('Error fetching direct expenses:', error);
      }
    };

    const fetchIndirectExpenses = async () => {
      try {
        const response = await fetch('https://backendcway.causewaycargo.com/superadmin/indirectexpense', {
          method: 'GET',
          headers: {
            auth: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
          },
        });
        const result = await response.json();
        console.log('Indirect Expenses Data:', result); // Log the full result
        const data = result.data; // Access the 'data' property
        if (Array.isArray(data)) {
          const indirectOptions = data.map((expense: any) => ({
            value: expense.id,
            label: expense.expenses_type,
          }));
          setIndirectExpenseOptions(indirectOptions);
        } else {
          console.error('Indirect expenses data is not an array:', data);
        }
      } catch (error) {
        console.error('Error fetching indirect expenses:', error);
      }
    };


    fetchDirectExpenses();
    fetchIndirectExpenses();
  }, []);

  useEffect(() => {
    // Fetch the shipment data from the API
    const fetchShipments = async () => {
      try {
        const response = await fetch(
          'https://backendcway.causewaycargo.com/superadmin/getallshipment',
          {
            method: 'GET',
            headers: {
              auth: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
            },
          }
        );
        const result = await response.json();

        if (Array.isArray(result.data)) {
          const shipmentOptions = result.data.map((shipment: Shipment) => ({
            value: shipment.shipment_id,
            label: shipment.shipment_id,
          }));
          setShipments(shipmentOptions);
        } else {
          console.error('Expected an array in the data field, got:', result.data);
          setShipments([]);
        }
      } catch (error) {
        console.error('Error fetching shipment data:', error);
        setShipments([]);
      }
    };

    fetchShipments();
  }, []);

  const handleExpenseTypeChange = (selectedOption: ExpenseOption | null) => {
    setExpenseType(selectedOption);

    // Clear selected expenses when expense type changes
    if (selectedOption?.value === 'Direct Expense') {
      setSelectedIndirectExpenses([]); // Clear indirect expenses
      // Add the id property to each item
      setExpenseTableData(directExpenseOptions.map(expense => ({
        id: generateId(),
        expenseName: expense.label,
        amount: '',
        comment: '',
        spendDate: null,
      })));
    } else if (selectedOption?.value === 'Indirect Expense') {
      setSelectedDirectExpenses([]); // Clear direct expenses
      // Add the id property to each item
      setExpenseTableData(indirectExpenseOptions.map(expense => ({
        id: generateId(),
        expenseName: expense.label,
        amount: '',
        comment: '',
        spendDate: null,
      })));
    } else {
      setExpenseTableData([]); // Clear all expenses if none selected
    }
  };

  const handleExpenseChange = (selectedOptions: MultiValue<ExpenseListOption>, actionMeta: ActionMeta<ExpenseListOption>) => {
    if (expenseType?.value === 'Direct Expense') {
      setSelectedDirectExpenses(selectedOptions as ExpenseListOption[]);
    } else if (expenseType?.value === 'Indirect Expense') {
      setSelectedIndirectExpenses(selectedOptions as ExpenseListOption[]);
    }
  };

  useEffect(() => {
    // Create initial table data when expenses are selected
    const createInitialTableData = (selectedExpenses: ExpenseListOption[]) => {
      return selectedExpenses.map((expense) => ({
        id: generateId(),
        expenseName: expense.label,
        amount: '',
        comment: '',
        spendDate: null,
      }));
    };

    if (expenseType?.value === 'Direct Expense') {
      setExpenseTableData(createInitialTableData(selectedDirectExpenses));
    } else if (expenseType?.value === 'Indirect Expense') {
      setExpenseTableData(createInitialTableData(selectedIndirectExpenses));
    }
  }, [selectedDirectExpenses, selectedIndirectExpenses, expenseType]);
  // Remove the second useEffect completely and modify the first one:
  useEffect(() => {
    // Only run this if we have selected expenses
    if (!expenseType) return;

    const createOrUpdateTableData = (selectedExpenses: ExpenseListOption[], allOptions: ExpenseListOption[]) => {
      // Filter to only the selected options from all options
      const selectedOptionData = allOptions.filter(option =>
        selectedExpenses.some(selected => selected.value === option.value)
      );

      // Map these to table data, preserving existing values where possible
      return selectedOptionData.map(option => {
        const existingEntry = expenseTableData.find(entry => entry.expenseName === option.label);
        return {
          id: existingEntry?.id || generateId(),
          expenseName: option.label,
          amount: existingEntry?.amount || '',
          comment: existingEntry?.comment || '',
          spendDate: existingEntry?.spendDate || null,
        };
      });
    };

    if (expenseType?.value === 'Direct Expense') {
      setExpenseTableData(createOrUpdateTableData(selectedDirectExpenses, directExpenseOptions));
    } else if (expenseType?.value === 'Indirect Expense') {
      setExpenseTableData(createOrUpdateTableData(selectedIndirectExpenses, indirectExpenseOptions));
    }
  }, [selectedDirectExpenses, selectedIndirectExpenses, expenseType, directExpenseOptions, indirectExpenseOptions]);



  const handleAmountChange = (index: number, value: string) => {
    const updatedData = [...expenseTableData];
    updatedData[index].amount = value;
    setExpenseTableData(updatedData);
  };

  const handleDateChange = (index: number, date: Date | null) => {
    const updatedData = [...expenseTableData];
    updatedData[index].spendDate = date;
    setExpenseTableData(updatedData);
  };

  const handleCommentChange = (index: number, value: string) => {
    if (value.length > 40) return; // Prevent exceeding 150 characters
    const updatedData = [...expenseTableData];
    updatedData[index].comment = value;
    setExpenseTableData(updatedData);
  };

  const handleAddRow = (expenseName: string) => {
    const newRow: ExpenseTableData = {
      id: generateId(),
      expenseName,
      amount: '',
      comment: '',
      spendDate: null,
    };
    setExpenseTableData([...expenseTableData, newRow]);
  };

  const handleRemoveRow = (id: string) => {
    setExpenseTableData(expenseTableData.filter(item => item.id !== id));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    let isValid = true;

    // Check if expense type is selected
    if (!expenseType) {
      toast.error('Please select expense type');
      isValid = false;
    }

    // If Direct Expense is selected, check if shipment is selected
    if (expenseType?.value === 'Direct Expense' && !selectedShipment) {
      toast.error('Please select a shipment');
      isValid = false;
    }

    // Check Direct Expense validations only if shipment is selected
    if (expenseType?.value === 'Direct Expense' && selectedShipment) {
      if (selectedDirectExpenses.length === 0) {
        toast.error('Please select at least one direct expense');
        isValid = false;
      }

      // Validate each expense in the table
      let hasTableErrors = false;
      expenseTableData.forEach((data) => {
        if (!data.amount || parseFloat(data.amount) <= 0) {
          toast.error(`Please enter a valid amount for ${data.expenseName}`);
          hasTableErrors = true;
        }

        if (!data.comment || data.comment.trim() === '') {
          toast.error(`Please enter a comment for ${data.expenseName}`);
          hasTableErrors = true;
        }

        if (!data.spendDate) {
          toast.error(`Please select a spend date for ${data.expenseName}`);
          hasTableErrors = true;
        }
      });

      if (hasTableErrors) {
        isValid = false;
      }
    }

    // Check Indirect Expense validation if selected
    if (expenseType?.value === 'Indirect Expense' && selectedIndirectExpenses.length === 0) {
      toast.error('Please select at least one indirect expense');
      isValid = false;
    }

    // If validation fails, stop submission
    if (!isValid) {
      return;
    }

    setLoading(true);

    // Transform expenseTableData into the format required by the API
    const formattedExpenses = expenseTableData.map((data) => ({
      expense_name: data.expenseName,
      amount: parseFloat(data.amount),
      comment: data.comment,
      spend_date: data.spendDate
        ? new Date(data.spendDate.getTime() - data.spendDate.getTimezoneOffset() * 60000)
          .toISOString()
          .split('T')[0]
        : '',
    }));

    // Construct the request body
    const requestBody: any = {
      expense_type: expenseType?.value,
      expenses_list: formattedExpenses,
    };

    // Only include shipment_id if expenseType is 'Direct Expense'
    if (expenseType?.value === 'Direct Expense') {
      requestBody.shipment_id = selectedShipment?.value;
    }

    try {
      const response = await fetch(
        'https://backendcway.causewaycargo.com/superadmin/addexpensives',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            auth: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do',
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('API Response:', result);
        // toast.success('Expenses added successfully!');

        setSuccess(true);
        setSelectedShipment(null);
        setExpenseType(null);
        setSelectedDirectExpenses([]);
        setSelectedIndirectExpenses([]);
        setExpenseTableData([]);

        setTimeout(() => history.push('/expenses'), 2000);
      } else {
        const errorData = await response.json().catch(() => null);
        console.error('API Error:', response.statusText, errorData);
        toast.error(errorData?.message || 'Failed to submit expenses. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Network error. Please check your connection and try again.');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };


  return (
    <div className='container-fluid bg-white py-12' style={{ overflow: 'auto', height: '550px' }}>
      <ToastContainer />
      <h2 className='mb-8'>Add Expenses</h2>
      <form onSubmit={handleSubmit}>
        <div className="row mb-6">
          <div className="col-md-4">
            <label htmlFor="expenseTypeSelect" className="form-label">Select Expense Type <span className="text-danger">*</span></label>
            <Select
              options={expenseOptions}
              value={expenseType}
              onChange={handleExpenseTypeChange}
              placeholder="Select Expense Type"
            />
          </div>

          {/* Show Shipment Select only if "Direct Expense" is selected */}
          {expenseType?.value === 'Direct Expense' && (
            <div className="col-md-4">
              <label htmlFor="shipmentSelect" className="form-label">Select Shipment <span className="text-danger">*</span></label>
              <Select
                options={shipments}
                value={selectedShipment}
                onChange={setSelectedShipment}
                placeholder="Select Shipment"
              />
            </div>
          )}

          {/* Show expense options based on expense type */}
          {expenseType && (
            <div className="col-md-4">
              <label htmlFor="expenseListSelect" className="form-label">
                {expenseType.value === 'Direct Expense' ? 'Select Direct Expense ' : 'Select Indirect Expense '}
                <span className="text-danger">*</span></label>
              <Select
                options={expenseType.value === 'Direct Expense' ? directExpenseOptions : indirectExpenseOptions}
                value={expenseType.value === 'Direct Expense' ? selectedDirectExpenses : selectedIndirectExpenses}
                onChange={handleExpenseChange}
                isMulti
                placeholder={expenseType.value === 'Direct Expense' ? 'Select Direct Expense' : 'Select Indirect Expense'}
                className ="text-capitalize"
              />
            </div>
          )}
        </div>

        {expenseTableData.length > 0 && (
          <div className='col-md-12' style={{ overflow: 'auto', height: '290px' }}>
            <h4 className="text-center text-secondary mb-4">Listed Data</h4>
            <div className='table-responsive'>
              <table className='table table-striped table-hover align-middle '>
                <thead>
                  <tr className='text-white' style={{ backgroundColor: '#343a40' }}>
                    <th className="text-center" style={{ paddingLeft: "6px" }}>
                      <div className='form-check form-check-sm form-check-custom form-check-solid'>
                        <input className='form-check-input' type='checkbox' disabled />
                      </div>
                    </th>
                    <th className="text-center">Expense Name <span className="text-danger">*</span></th>
                    <th className="text-center">Amount <span className="text-danger">*</span></th>
                    <th className="text-center">Comment <span className="text-danger">*</span> </th>
                    <th className="text-center">Spend Date <span className="text-danger">*</span></th>
                    <th className="text-center">Actions<span className="text-danger">*</span></th>
                  </tr>
                </thead>
                <tbody>
                  {expenseTableData.map((data, index) => (
                    <tr key={index} >
                      <td className="text-center" style={{ paddingLeft: "6px" }}>
                        <div className='form-check form-check-sm form-check-custom form-check-solid'>
                          <input className='form-check-input' type='checkbox' />
                        </div>
                      </td>
                      <td className="text-center">{data.expenseName}</td>
                      <td className="text-center">
                        <input
                          type="text"
                          required
                          className="form-control form-control-sm"
                          value={data.amount}
                          onChange={(e) => handleAmountChange(index, e.target.value)}
                          onKeyDown={(e) => {
                            if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete') {
                              e.preventDefault();
                            }
                          }}
                        />
                      </td>
                      <td className="text-center">
                        <input
                          type="text"
                          required
                          className="form-control form-control-sm"
                          value={data.comment}
                          maxLength={40} // Restrict input length
                          onChange={(e) => handleCommentChange(index, e.target.value)}
                        />
                      </td>
                      <td className="text-center">
                        <DatePicker
                          selected={data.spendDate}
                          onChange={(date) => handleDateChange(index, date)}
                          dateFormat="dd-MM-yyyy"
                          className="form-control form-control-sm"
                          placeholderText="Select a date"
                          maxDate={new Date()}
                          required
                        />
                      </td>
                      <td className="text-center">
                        <div className="btn-group">
                          <button
                            type="button"
                            className="btn btn-sm btn-icon btn-light-primary"
                            onClick={() => handleAddRow(data.expenseName)}
                            title="Add another row for this expense"
                          >
                            <i className="fas fa-plus"></i>
                          </button>
                          {expenseTableData.filter(item => item.expenseName === data.expenseName).length > 1 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-icon btn-light-danger ms-2"
                              onClick={() => handleRemoveRow(data.id)}
                              title="Remove this row"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className='d-flex justify-content-end gap-5'>
          <Link className="btn btn-primary " to="/expenses">
            Cancel
          </Link>
          <button
            type='submit'
            className='btn btn-primary'
            disabled={loading}
          >
            {!loading && <span className='indicator-label'>Submit</span>}
            {loading && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>

        </div>
      </form>
      {success && <div className='modal-backdrop shadow-lg rounded-5'>
        <div className='modal fade show d-block ' tabIndex={-1} role='dialog'>
          <div className='modal-dialog modal-dialog-centered' role='document'>
            <div className='modal-content pb-10 pt-4'>
              <div className='modal-header py-2 d-flex justify-content-end'>
                {/* <h5 className='modal-title'>Edit Customer</h5> */}
                {/* <button type='button' className='close' onClick={() => setSuccess(false)}>
 <span aria-hidden='true'>&times;</span>
 </button> */}

                <div className='modal-header py-2 d-flex justify-content-end'>
                  <span className='close-btn' onClick={() => {
                    setSuccess(false);
                    history.push('/expenses');
                  }}>🗙</span>
                </div>

              </div>
              <div className='text-center '>
                <img alt='Logo' src={toAbsoluteUrl('/media/illustrations/sigma-1/11.png')} className='w-250px mb-6 runningImage' />
                <h2>Expenses Added successfully!</h2>
              </div>

            </div>
          </div>
        </div>
      </div>}
    </div>
  );
}